import React, {
  useState,
  useRef,
  useEffect,
  useCallback
} from 'react';

import styles from './Carousel.module.less';

export default WrappedComponent => {
  return ({
    type = 'slide',
    gesture = false,
    autoplay = false,
    size = 1,
    data = [],
    interval = 4000,
    duration = 300,
    ...passThroughProps
  }) => {
    if (!data || data.length <= 0)  return null;

    size = Number(size);
    interval = Number(interval);
    duration = type === 'fade' ? 800 : Number(duration);
    let displayData = []
    if (size === 1) {
      displayData = [...data];
    } else {
      for(let i = 0, count = Math.ceil(data.length / size); i < count; i++) {
        displayData.push(data.slice(i * size, (i + 1) * size));
      }
    }

    const [indexTrack, setIndexTrack] = useState([0, 0]);
    const $el = useRef(null);
    const direction = useRef(true);
    const timer = useRef(null);

    useEffect(() => {
      if (!$el.current || indexTrack[1] === indexTrack[0]) return;

      $el.current.classList.remove('ready');
      $el.current.classList.remove('next');
      $el.current.classList.remove('prev');
      $el.current.classList.remove('active');
      $el.current.classList.add(direction.current ? 'next' : 'prev');
      [...$el.current.children].forEach(el => {
        el.classList.remove('enter')
        el.classList.remove('leave')
      })
      $el.current.children[indexTrack[0]].classList.add('leave')
      $el.current.children[indexTrack[1]].classList.add('enter')
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          $el && $el.current && $el.current.classList.add('active');
        });
      });
    }, [indexTrack]);

    const next = useCallback(() => {
      direction.current = true;
      setIndexTrack(([prevIndex, currentIndex]) => {
        let nextIndex = currentIndex + 1;
        nextIndex = nextIndex >= displayData.length ? 0 : nextIndex;
        return [currentIndex, nextIndex];
      });
    }, []);

    const prev = useCallback(() => {
      direction.current = false;
      setIndexTrack(([prevIndex, currentIndex]) => {
        let nextIndex = currentIndex - 1;
        nextIndex = nextIndex < 0 ? displayData.length - 1 : nextIndex;
        return [currentIndex, nextIndex];
      });
    }, []);

    const turnPage = useCallback(nextIndex => {
      nextIndex = nextIndex >= displayData.length ? 0 : nextIndex;
      nextIndex = nextIndex < 0 ? displayData.length - 1 : nextIndex;

      const currentIndex = indexTrack[1];
      if (nextIndex === currentIndex) return;
      direction.current = nextIndex > currentIndex;
      setIndexTrack([currentIndex, nextIndex]);
    }, [indexTrack]);

    const play = useCallback(() => {
      if (!autoplay || displayData.length <= 1) return;

      clearTimeout(timer.current);
      let _interval = interval;

      const _loop = () => {
        timer.current = setTimeout(() => {
          next();
          _loop();
        }, _interval);
        _interval = interval + duration;
      };
      _loop();
    }, [])

    const stop = useCallback(() => {
      clearTimeout(timer.current);
    }, []);

    const _mouse = useRef({
      stop: false,
      start: 0,
    });
    const mousedownHandle = useCallback(e => {
      _mouse.current.stop = false;
      _mouse.current.start = e.clientX;
    }, []);
    const mouseupHandle = useCallback(e => {
      if (e.clientX - _mouse.current.start >= 10) {
        _mouse.current.stop = true;
        prev();
      } else if (e.clientX - _mouse.current.start <= -10) {
        _mouse.current.stop = true;
        next();
      }
    }, []);
    const clickHandle = useCallback(e => {
      if (_mouse.current.stop) {
        e.stopPropagation();
      }
    }, []);

    useEffect(() => {
      if (!$el.current) return

      $el.current.classList.add(styles.Carousel);
      if (styles[type]) {
        $el.current.classList.add(styles[type]);
      }
      $el.current.classList.add('next');
      $el.current.children[indexTrack[1]].classList.add('enter');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          $el && $el.current && $el.current.classList.add('ready');
        });
      });

      play();

      $el.current.addEventListener('mouseenter', stop);
      $el.current.addEventListener('mouseleave', play);
      if (gesture) {
        $el.current.addEventListener('mousedown', mousedownHandle, true);
        $el.current.addEventListener('mouseup', mouseupHandle, true);
        $el.current.addEventListener('click', clickHandle, true);
      }
      return () => {
        stop();
        $el.current.removeEventListener('mouseenter', stop);
        $el.current.removeEventListener('mouseleave', play);
        if (gesture) {
          $el.current.removeEventListener('mousedown', mousedownHandle, true);
          $el.current.removeEventListener('mouseup', mouseupHandle, true);
          $el.current.removeEventListener('click', clickHandle, true);
        }
      };
    }, []);

    return (
      <WrappedComponent
        $el={$el}
        data={displayData}
        index={indexTrack[1]}
        count={displayData.length}
        next={next}
        prev={prev}
        turnPage={turnPage}
        play={play}
        stop={stop}
        {...passThroughProps}
      />
    );
  };
};
